type TimeUnit = 'y' | 'M' | 'w' | 'd' | 'h' | 'H' | 'm' | 's';

function adjustDate(date: Date, amount: number, unit: TimeUnit) {
  switch (unit) {
    case 'y':
      date.setFullYear(date.getFullYear() + amount);
      break;
    case 'M':
      date.setMonth(date.getMonth() + amount);
      break;
    case 'w':
      date.setDate(date.getDate() + 7 * amount);
      break;
    case 'd':
      date.setDate(date.getDate() + amount);
      break;
    case 'h':
    case 'H':
      date.setHours(date.getHours() + amount);
      break;
    case 'm':
      date.setMinutes(date.getMinutes() + amount);
      break;
    case 's':
      date.setSeconds(date.getSeconds() + amount);
      break;
    default:
      throw new Error(`Invalid time unit: ${unit}`);
  }
}

function translateUnit(unit: TimeUnit): string {
  switch (unit) {
    case 'y':
      return 'year';
    case 'M':
      return 'month';
    case 'w':
      return 'week';
    case 'd':
      return 'day';
    case 'h':
    case 'H':
      return 'hour';
    case 'm':
      return 'minute';
    case 's':
      return 'second';
    default:
      throw new Error(`Invalid time unit: ${unit}`);
  }
}

/*
Python:
import re

regex = re.compile(r'(?:now|(?:\d{4}\.\d{2}\.\d{2}\|\|))((?:[+-]\d+[yMwdhHms]\/?d?)*)?')
matches = regex.match(input) 

Scala:
import scala.util.matching.Regex

val regex: Regex = "(?:now|(?:\\d{4}\\.\\d{2}\\.\\d{2}\\|\\|))((?:[+-]\\d+[yMwdhHms]/?d?)*)?".r
val matches = regex.findFirstMatchIn(input)
*/

const Regex = /(?:now|(?:\d{4}\.\d{2}\.\d{2}\|\|))((?:[+-]\d+[yMwdhHms]\/?d?)*)?/;
export const isDateMathExpression = (dateExpression: unknown): dateExpression is string =>
  typeof dateExpression === 'string' && Regex.test(dateExpression) ? true : false;

export function parseDateMathExpressionToSQL(dateExpression: string): string {
  let input = dateExpression;
  let sql: string;
  if (input.startsWith('now')) {
    input = input.slice(3);
    sql = 'now()';
  } else {
    const match = input.match(/(.+)\|\|/);
    if (!match) {
      throw new Error(`Invalid date math string: ${dateExpression}`);
    }
    const dateString = match[1];
    input = input.slice(dateString.length + 2);
    sql = "('" + dateString.replace(/\./g, '-') + "')::date";
  }
  if (input) {
    const operations = input.match(/([+-]\d+[yMwdhHms]\/?d?)/g);
    if (!operations) {
      throw new Error(`Invalid operations: ${dateExpression}`);
    }

    for (const op of operations) {
      const match = op.match(/([+-])(\d+)([yMwdhHms])(?:\/([d]))?/);
      if (!match) {
        throw new Error(`Invalid operation: ${op} in ${dateExpression}`);
      }
      const [_, sign, amount, unit, round] = match;
      const interval = `${sign === '+' ? '+' : '-'} interval '${amount} ${translateUnit(unit as TimeUnit)}'`;
      sql += ` ${interval}`;
      if (round) {
        switch (round) {
          case 'd':
            sql = `(${sql})::date`;
            break;
          default:
            throw new Error(`Invalid date roundType: ${round} in ${dateExpression}`);
        }
      }
    }
  }
  return sql;
}

export function parseDateMathExpression(dateExpression: string) {
  if (!isDateMathExpression(dateExpression)) {
    throw new Error(`Invalid date math string: ${dateExpression}`);
  }
  let input = dateExpression;
  let date: Date;
  if (input.startsWith('now')) {
    date = new Date();
    input = input.slice(3);
  } else {
    const match = input.match(/(.+)\|\|/);
    if (!match) {
      throw new Error(`Invalid date math string: ${dateExpression}`);
    }

    const dateString = match[1];
    date = new Date(dateString.replace(/\./g, '-'));
    if (isNaN(date.getTime())) {
      throw new Error(`Invalid date string: ${dateString} in ${dateExpression}`);
    }

    input = input.slice(dateString.length + 2);
  }
  if (input) {
    const operations = input.match(/([+-]\d+[yMwdhHms]\/?d?)/g);
    if (!operations) {
      throw new Error(`Invalid operations: ${dateExpression}`);
    }
    for (const op of operations) {
      const match = op.match(/([+-])(\d+)([yMwdhHms])(?:\/([d]))?/);
      if (!match) {
        throw new Error(`Invalid operation: ${op} in ${dateExpression}`);
      }
      const [_, sign, amount, unit, round] = match;
      adjustDate(date, Number(sign + amount), unit as TimeUnit);
      if (round) {
        switch (round) {
          case 'd':
            date.setHours(0, 0, 0, 0);
            break;
          default:
            throw new Error(`Invalid date roundType: ${round} in ${dateExpression}`);
        }
      }
    }
  }
  return date;
}

// Testing
function _test() {
  console.log(parseDateMathExpression('now')); // 2001-01-01T13:00:00.000Z
  console.log(parseDateMathExpressionToSQL('now')); // 2001-01-01T13:00:00.000Z
  console.log(parseDateMathExpression('now-1y+1y/d')); // 2001-01-01T11:00:00.000Z
  console.log(parseDateMathExpressionToSQL('now-1y+1y/d')); // 2001-01-01T11:00:00.000Z
  console.log(parseDateMathExpression('now-1h/d')); // 2001-01-01T00:00:00.000Z
  console.log(parseDateMathExpressionToSQL('now-1h/d')); // 2001-01-01T00:00:00.000Z
  console.log(parseDateMathExpression('2001.02.01||+12m')); // 2001-03-01T00:00:00.000Z
  console.log(parseDateMathExpressionToSQL('2001.02.01||+12m')); // 2001-03-01T00:00:00.000Z
}

// _test();
